// Little confirmation / error messages that appear at the bottom of the screen and fade away on their own.

import { createContext, use, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { StoreError } from '../store/errors';
import { Icon } from './Icon';

export type ToastKind = 'ok' | 'error';
type Toast = (message: string, kind?: ToastKind) => void;

interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
}

const ToastContext = createContext<Toast | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(0);
  const box = useRef<HTMLDivElement>(null);

  const toast = useCallback<Toast>((message, kind = 'ok') => {
    const id = nextId.current++;
    setItems((list) => [...list, { id, message, kind }]);
    setTimeout(() => setItems((list) => list.filter((t) => t.id !== id)), kind === 'error' ? 7000 : 4000);
  }, []);

  // A popover lives in the top layer, so showing it again keeps toasts above any dialog that opened after them.
  useEffect(() => {
    const el = box.current;
    if (!el || !items.length) return;
    try {
      el.hidePopover();
      el.showPopover();
    } catch {
      /* popovers unsupported: toasts fall back to normal stacking */
    }
  }, [items]);

  return (
    <ToastContext value={toast}>
      {children}
      <div id="toasts" ref={box} popover="manual" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind}`}>
            <Icon name={t.kind === 'error' ? 'alert' : 'check-circle'} size={18} />
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </ToastContext>
  );
}

export function useToast(): Toast {
  const toast = use(ToastContext);
  if (!toast) throw new Error('useToast must be used inside <ToastProvider>.');
  return toast;
}

/**
 * Wraps a change to the data: shows `okMessage` when it works and a plain-English error when it doesn't.
 * Resolves to whether it worked, so the caller can leave a dialog open or reset a field.
 */
export function useRun(): (work: Promise<unknown>, okMessage?: string) => Promise<boolean> {
  const toast = useToast();
  return useCallback(
    async (work, okMessage) => {
      try {
        await work;
        if (okMessage) toast(okMessage);
        return true;
      } catch (e) {
        console.error(e);
        // A reason written for people ("…Move them to another team lead first.") is shown as it is.
        toast(e instanceof StoreError ? e.message : 'That did not work. You may not have permission, or the connection dropped.', 'error');
        return false;
      }
    },
    [toast],
  );
}
